<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div>
    <save-skill
      v-if="showSaveModal"
      :http="http"
      :api="skillsEndpoint"
      :levels="levels"
      @close="onSaveModalClose"
    ></save-skill>
    <edit-skill
      v-if="showEditModal"
      :http="http"
      :data="editModalState"
      :levels="levels"
      @close="onEditModalClose"
    ></edit-skill>
    <div class="orangehrm-horizontal-padding orangehrm-vertical-padding">
      <profile-action-header v-if="!isEmployee" @click="onClickAdd">
        {{ $t('general.skills') }}
      </profile-action-header>
      <profile-title-header v-else>
        {{ $t('general.skills') }}
      </profile-title-header>
    </div>
    <table-header
      :selected="checkedItems.length"
      :total="total"
      :loading="isLoading"
      @delete="onClickDeleteSelected"
    ></table-header>
    <div class="orangehrm-container">
      <oxd-card-table
        v-model:selected="checkedItems"
        :headers="headers"
        :items="items?.data"
        :selectable="isEmployee ? false : true"
        :clickable="false"
        :loading="isLoading"
        :disabled="isDisabled"
        row-decorator="oxd-table-decorator-card"
      />
    </div>
    <div v-if="showPaginator" class="orangehrm-bottom-container">
      <oxd-pagination v-model:current="currentPage" :length="pages" />
    </div>
    <delete-confirmation ref="deleteDialog"></delete-confirmation>
  </div>
</template>

<script>
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import {APIService} from '@ohrm/core/util/services/api.service';
import ProfileActionHeader from '@/orangehrmPimPlugin/components/ProfileActionHeader';
import ProfileTitleHeader from '@/orangehrmPimPlugin/components/ProfileTitleHeader';
import SaveSkill from '@/orangehrmPimPlugin/components/SaveSkill';
import EditSkill from '@/orangehrmPimPlugin/components/EditSkill';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';

export default {
  name: 'EmployeeSkills',

  components: {
    'profile-action-header': ProfileActionHeader,
    'profile-title-header': ProfileTitleHeader,
    'save-skill': SaveSkill,
    'edit-skill': EditSkill,
    'delete-confirmation': DeleteConfirmationDialog,
  },

  props: {
    employeeId: {
      type: String,
      required: true,
    },
    levels: {
      type: Array,
      required: true,
    },
  },

  setup(props) {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `api/v2/pim/employees/${props.employeeId}/skills`,
    );

    const skillsEndpoint = `api/v2/pim/employees/${props.employeeId}/skills/allowed`;

    const skillNormalizer = data => {
      return data.map(item => {
        const level = props.levels?.find(level => level.id === item.level);
        return {
          id: item.skill.id,
          name: item.skill.name,
          yearsOfExperience: item.yearsOfExperience,
          comments: item.comments,
          level: level.label,
        };
      });
    };

    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {normalizer: skillNormalizer, toastNoRecords: false});
    return {
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
      skillsEndpoint,
    };
  },

  data() {
    return {
      headers: [
        {
          name: 'name',
          slot: 'title',
          title: this.$t('pim.skill'),
          style: {flex: 1},
        },
        {
          name: 'yearsOfExperience',
          title: this.$t('pim.years_of_experience'),
          style: {flex: 1},
        },
        {
          name: 'level',
          title: this.$t('general.level'),
          style: {flex: 1},
        },
        {
          name: 'actions',
          slot: 'action',
          title: this.$t('general.actions'),
          style: {'flex-basis': '10em'},
          cellType: 'oxd-table-cell-actions',
          cellConfig: {
            delete: {
              onClick: this.onClickDelete,
              component: 'oxd-icon-button',
              props: {
                name: 'trash',
              },
            },
            edit: {
              onClick: this.onClickEdit,
              props: {
                name: 'pencil-fill',
              },
            },
          },
        },
      ],
      checkedItems: [],
      showSaveModal: false,
      showEditModal: false,
      editModalState: null,
    };
  },

  computed: {
    isDisabled() {
      return this.isEmployee || this.showSaveModal || this.showEditModal;
    },
    isViewer() {
      return window.appGlobal.user.id != parseInt(this.employeeId)
        ? true
        : false;
    },
    isEmployee() {
      return [2, 3].includes(window.appGlobal.user.roleId) && this.isViewer
        ? true
        : false;
    },
  },

  methods: {
    onClickDeleteSelected() {
      const ids = this.checkedItems.map(index => {
        return this.items?.data[index].id;
      });
      this.$refs.deleteDialog.showDialog().then(confirmation => {
        if (confirmation === 'ok') {
          this.deleteItems(ids);
        }
      });
    },
    onClickDelete(item) {
      this.$refs.deleteDialog.showDialog().then(confirmation => {
        if (confirmation === 'ok') {
          this.deleteItems([item.id]);
        }
      });
    },
    deleteItems(items) {
      if (items instanceof Array) {
        this.isLoading = true;
        this.http
          .deleteAll({
            ids: items,
          })
          .then(() => {
            return this.$toast.deleteSuccess();
          })
          .then(() => {
            this.isLoading = false;
            this.resetDataTable();
          });
      }
    },
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery();
    },
    onClickAdd() {
      this.showEditModal = false;
      this.editModalState = null;
      this.showSaveModal = true;
    },
    onClickEdit(item) {
      this.showSaveModal = false;
      this.editModalState = item;
      this.showEditModal = true;
    },
    onSaveModalClose() {
      this.showSaveModal = false;
      this.resetDataTable();
    },
    onEditModalClose() {
      this.showEditModal = false;
      this.editModalState = null;
      this.resetDataTable();
    },
  },
};
</script>
